import {
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React from 'react'
import { useCursorExpandingCircle } from 'src/hooks'
import Lnk, { LnkProps } from 'src/navigation/Lnk'
import { ScaleAndFade } from 'src/style/keyframeUtils'
import variables from 'src/style/variables.json'
import { css } from 'styled-components'

export enum ButtonDesigns {
  designCta = 'designCta',
  designCtaInverse = 'designCtaInverse',
  designRed = 'designRed',
  plainNoShadow = 'plainNoShadow',
  designUtilityToolbar = 'designUtilityToolbar',
}

export enum ButtonTypes {
  submit = 'submit',
  reset = 'reset',
  button = 'button',
}

export interface ButtonProps {
  design: ButtonDesigns
  type: ButtonTypes
  className?: string
  grayed?: boolean
  disabled?: boolean
  onClick?: React.MouseEventHandler
  loading?: boolean
  loadingText?: string | React.ReactElement
  text?: string | React.ReactElement
  image?: React.ReactElement
  big?: boolean
  linkProps?: LnkProps
  id?: string
  testId?: string
  'data-testid'?: string
}

const circleRadius = 20
/** @deprecated - Please use `ext-ui/Button/Button` instead */
const Button: React.FC<ButtonProps> = (props): React.ReactElement => {
  const cursorClickHandler =
    props.linkProps === undefined ? props.onClick : undefined
  const { animationModifiers, buttonRef, circleCoordinates, handleClick } =
    useCursorExpandingCircle<HTMLButtonElement>(
      circleRadius,
      cursorClickHandler,
    )

  const innerContent = (
    <>
      {props.loading && (
        <div
          css={css`
            margin-right: 6px;
            display: flex;
          `}
        >
          <Processing
            isLod={
              props.design === ButtonDesigns.designCta ||
              props.design === ButtonDesigns.designRed
            }
          />
        </div>
      )}
      <div
        style={{
          left: circleCoordinates.x,
          top: circleCoordinates.y,
          height: circleRadius * 2,
          width: circleRadius * 2,
        }}
        css={css`
          position: absolute;
          border-radius: 50%;
          background-color: ${themeConstants.colors.cta};
          opacity: 0;

          ${animationModifiers.isAnimated &&
          css`
            ${ScaleAndFade()}
          `}

          ${props.design === ButtonDesigns.designCta &&
          css`
            background-color: ${themeConstants.colors.cta};
          `}

          ${(props.design === ButtonDesigns.designCtaInverse ||
            props.design === ButtonDesigns.designUtilityToolbar) &&
          css`
            background-color: ${styleConstants.colors.gray_light};
          `}
        `}
      />
      <span
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        {props.image && !props.loading && (
          <span
            css={css`
              margin-right: 5px;
              ${props.design === ButtonDesigns.designUtilityToolbar &&
              css`
                max-width: ${variables.units['base-unit-4']}px;
                max-height: ${variables.units['base-unit-4']}px;
                padding: ${variables.units['base-unit-0']}px
                  ${variables.units['base-unit-1']}px;
                margin-right: ${variables.units['base-unit-2']}px;
              `}
            `}
          >
            {props.image}
          </span>
        )}
        <span>
          {props.loading && props.loadingText ? props.loadingText : props.text}
        </span>
      </span>
    </>
  )

  const buttonStyling = css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    cursor: pointer;
    padding: 16px 20px;
    border-radius: ${styleConstants.borderRadius.button};
    font-size: ${props.design === ButtonDesigns.designUtilityToolbar
      ? '12px'
      : '14px'};
    transition:
      background-color ${styleConstants.transitionSpeed.slow} ease-out,
      box-shadow ${styleConstants.transitionSpeed.slow} ease-out,
      opacity ${styleConstants.transitionSpeed.slow} ease-out;
    overflow: hidden;
    height: 38px;
    min-height: 38px;
    text-align: center;
    line-height: normal;
    font-family: ${props.design === ButtonDesigns.designUtilityToolbar
      ? styleConstants.fontFamilies.regular
      : styleConstants.fontFamilies.medium};

    ${props.big === true &&
    css`
      height: 48px;
      padding: 16px 30px;
    `}

    ${props.design === ButtonDesigns.designCta &&
    css`
      background-color: ${themeConstants.colors.cta};
      box-shadow:
        0 4px 10px 0 rgba(0, 0, 0, 0.15),
        inset 0px 0px 0px 2px ${themeConstants.colors.cta_dark};
      color: ${styleConstants.colors.gray_lightest};
      &:hover {
        background-color: ${themeConstants.colors.cta_dark};
        box-shadow:
          0 2px 5px 0 rgba(0, 0, 0, 0.15),
          inset 0px 0px 0px 2px ${themeConstants.colors.cta_dark};
      }
    `}

    ${props.design === ButtonDesigns.designCtaInverse &&
    css`
      background-color: ${styleConstants.colors.gray_lightest};
      box-shadow:
        0 3px 10px 0 rgba(0, 0, 0, 0.1),
        inset 0px 0px 0px 1px ${styleConstants.colors.gray_medium};
      color: ${themeConstants.colors.cta};
      &:hover {
        box-shadow:
          0 1px 3px 0 rgba(0, 0, 0, 0.1),
          inset 0px 0px 0px 1px ${styleConstants.colors.gray_medium};
      }
    `}

    ${props.design === ButtonDesigns.designRed &&
    css`
      background-color: ${styleConstants.colors.red};
      box-shadow:
        0 4px 10px 0 rgba(0, 0, 0, 0.15),
        inset 0px 0px 0px 2px ${styleConstants.colors.red_dark};
      color: ${styleConstants.colors.gray_lightest};
      &:hover {
        background-color: ${styleConstants.colors.red_dark};
        box-shadow:
          0 2px 5px 0 rgba(0, 0, 0, 0.15),
          inset 0px 0px 0px 2px ${styleConstants.colors.red_dark};
      }
    `}

    ${props.design === ButtonDesigns.plainNoShadow &&
    css`
      background-color: ${styleConstants.colors.gray_lightest};
      color: ${themeConstants.colors.font_primary};
      &:hover {
        color: ${themeConstants.colors.cta};
      }
    `}

    ${props.design === ButtonDesigns.designUtilityToolbar &&
    css`
      border-radius: 6px;
      border: 1px solid ${variables.tokensColor.grey400};
      background-color: ${variables.tokensColor.grey100};
      color: ${themeConstants.colors.cta};
    `}

    ${(props.disabled === true || props.loading === true) &&
    css`
      opacity: 0.5;
      cursor: default;
    `}

    ${props.grayed === true &&
    css`
      opacity: 0.5;
    `}
  `

  const dataTestId = props['data-testid'] ?? props.testId ?? props.id ?? ''

  return props.linkProps !== undefined && !(props.disabled || props.loading) ? (
    <Lnk
      id={props.id}
      testId={dataTestId}
      onClick={props.onClick}
      {...props.linkProps}
    >
      <span
        ref={buttonRef}
        className={props.className}
        css={buttonStyling}
        onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
          if (!(props.disabled === true || props.loading === true)) {
            handleClick(e)
          }
        }}
        onKeyPress={(): void => {
          return
        }}
        tabIndex={-1}
        role={'button'}
      >
        {innerContent}
      </span>
    </Lnk>
  ) : (
    <button
      id={props.id ? props.id : 'button'}
      data-testid={dataTestId}
      ref={buttonRef}
      className={props.className}
      css={buttonStyling}
      type={props.type}
      disabled={props.disabled || props.loading}
      onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
        if (!(props.disabled === true || props.loading === true)) {
          handleClick(e)
        }
      }}
    >
      {innerContent}
    </button>
  )
}

export default Button
